import Badge, { type BadgeTone } from '@/components/base/Badge';
import type { Permission, PermissionGroup, PermissionRisk } from '@/types/portal';

/** For each permission: the production permission it is saved as, and the permissions sharing it. */
export type PermissionLinks = Partial<Record<Permission, { production: string; together: Permission[] }>>;

interface PermissionMatrixProps {
  catalog: PermissionGroup[];
  permissions: Permission[];
  onToggle: (key: Permission) => void;
  onToggleGroup: (keys: Permission[], on: boolean) => void;
  disabled: boolean;
  /** Permissions that production saves together switch together; each shows which. */
  links?: PermissionLinks;
}

const riskTone: Record<PermissionRisk, BadgeTone> = {
  Standard: 'outline',
  Sensitive: 'secondary',
  Critical: 'primary',
};

function Switch({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? 'bg-primary-500' : 'bg-background-300'
      } ${disabled ? '' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-background-50 transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

export default function PermissionMatrix({
  catalog,
  permissions,
  onToggle,
  onToggleGroup,
  disabled,
  links = {},
}: PermissionMatrixProps) {
  const granted = new Set(permissions);
  const labelOf = (key: Permission) =>
    catalog.flatMap((group) => group.permissions).find((permission) => permission.key === key)?.label ?? key;

  return (
    <div className="space-y-4">
      {catalog.map((group) => {
        const keys = group.permissions.map((permission) => permission.key);
        const grantedCount = keys.filter((key) => granted.has(key)).length;
        const allOn = grantedCount === keys.length;

        return (
          <section key={group.id} className="rounded-lg border border-background-200 bg-background-50">
            <header className="flex items-center justify-between gap-3 border-b border-background-200 px-5 py-3.5">
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-100 text-base text-foreground-600">
                  <i className={group.icon} />
                </span>
                <div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-950">
                    {group.label}
                  </h3>
                  <p className="text-[11px] text-foreground-500">
                    {grantedCount} of {keys.length} granted
                  </p>
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs text-foreground-500">
                <span className="hidden sm:inline">{allOn ? 'All' : 'Select all'}</span>
                <Switch
                  checked={allOn}
                  disabled={disabled}
                  onChange={() => onToggleGroup(keys, !allOn)}
                  label={`Toggle all ${group.label} permissions`}
                />
              </label>
            </header>

            <ul className="divide-y divide-background-100">
              {group.permissions.map((permission) => {
                const on = granted.has(permission.key);
                return (
                  <li
                    key={permission.key}
                    className="flex items-center justify-between gap-4 px-5 py-3"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm text-foreground-900">{permission.label}</span>
                        {permission.risk !== 'Standard' ? (
                          <Badge tone={riskTone[permission.risk]}>{permission.risk}</Badge>
                        ) : null}
                      </div>
                      <p className="mt-0.5 text-xs text-foreground-500">{permission.description}</p>
                      <p className="mt-0.5 font-mono text-[11px] text-foreground-400">
                        {permission.key}
                      </p>
                      {(links[permission.key]?.together.length ?? 0) > 1 ? (
                        <p className="mt-0.5 text-[11px] text-secondary-900">
                          <i className="ri-links-line mr-1" />
                          Saved together with{' '}
                          {(links[permission.key]?.together ?? [])
                            .filter((key) => key !== permission.key)
                            .map(labelOf)
                            .join(', ')}{' '}
                          in production
                        </p>
                      ) : null}
                    </div>
                    <Switch
                      checked={on}
                      disabled={disabled}
                      onChange={() => onToggle(permission.key)}
                      label={`Toggle ${permission.label}`}
                    />
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}